/**
 * BIM-style contextual Modify tools — interactive viewport editors.
 *
 * Every class here is a tool driven by the contextual "Modify | <Category>"
 * ribbon tab. They all follow the same lifecycle:
 *  1. activate() reads the current selection (never clears it),
 *  2. invalid selection -> notify + fall back to SELECT,
 *  3. pointer edits produce a live 'polygon'/'point' preview,
 *  4. Enter (or loop close) commits through bimStore so the change is undoable,
 *  5. Escape / commit returns control to SELECT keeping the selection.
 *
 * World units are meters; geometry stores XY plan coordinates with Z up.
 */
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import { BIMElement, WallElement, SlabShapePoint } from '../../core/types';
import { elementWorldBounds, getSelectedElements } from '../../utils/contextualModelUtils';
import { useSubElementStore } from '../../state/contextualSessionStore';
import i18n from '../../i18n';

/* ==================== shared 2D helpers ==================== */

const SURFACE_CLOSE_TOLERANCE = 0.2; // meters — loop-close radius

function pointInPolygon2D(x: number, y: number, polygon: [number, number][]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [xi, yi] = polygon[i];
        const [xj, yj] = polygon[j];
        const intersect = ((yi > y) !== (yj > y)) &&
            (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}

/** Boundary data accessor across the surface-family categories. */
function getSurfaceBoundary(el: BIMElement): [number, number][] | null {
    const g = (el as any).geometryData;
    if (!g) return null;
    if (Array.isArray(g.boundary) && g.boundary.length >= 3) return g.boundary;
    if (Array.isArray(g.footprint) && g.footprint.length >= 3) return g.footprint;
    return null;
}

/** Which geometryData key carries the boundary for this element kind. */
function boundaryKey(el: BIMElement): 'boundary' | 'footprint' {
    return el.category === 'Roofs' ? 'footprint' : 'boundary';
}

/**
 * Base class for click-sequence polygon editors (profile / boundary editing).
 */
abstract class PolygonSketchTool extends BaseTool {
    protected points: THREE.Vector3[] = [];
    protected currentPoint: THREE.Vector3 | null = null;
    /** element currently being edited */
    protected target: BIMElement | null = null;

    protected beginSelection(): boolean {
        const els = getSelectedElements();
        if (els.length === 0) {
            this.exitToSelect(i18n.t('tools.notify.selectAnElementFirst'));
            return false;
        }
        this.target = els[0];
        return true;
    }

    protected onSketchPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.target) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point, 0.1);

        // Close the loop when clicking near the first point
        if (this.points.length > 2 && snapped.distanceTo(this.points[0]) < SURFACE_CLOSE_TOLERANCE) {
            this.finish();
            return;
        }
        this.points.push(snapped);
        this.updatePreview();
    }

    protected onSketchPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point, 0.1);
            this.updatePreview();
        }
    }

    protected updatePreview(): void {
        if (!this.context) return;
        if (this.points.length > 0) {
            const previewPoints = this.currentPoint
                ? [...this.points, this.currentPoint]
                : [...this.points];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: true,
                color: '#00b4ff',
            });
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        this.onSketchPointerMove(event);
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finish();
                return;
            }
            this.notify(i18n.t('tools.notify.needAtLeast3PointsTo'), 'warning');
            return;
        }
        if (event.key === 'Escape' && this.points.length > 0) {
            this.points = [];
            this.currentPoint = null;
            this.context?.appStore.setToolPreview(null);
            return;
        }
        super.onKeyDown(event);
    }

    /** Commit the finished loop; called with >= 3 closed points. */
    protected abstract finish(): void;

    protected exitToSelect(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }
}

/* ==================== WALL: Edit Profile ==================== */

/**
 * Edit Profile (Walls): defines a custom plan footprint polygon for the wall.
 * The committed profile is consumed by the wall renderer (extruded vertically)
 * exactly like the auto-generated join profiles.
 */
export class EditWallProfileTool extends PolygonSketchTool {
    id = 'CTX_EDIT_PROFILE';
    name = 'Edit Profile';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        this.onSketchPointerDown(event);
    }

    activate(context: any): void {
        super.activate(context);
        if (!this.beginSelection()) return;
        if ((this.target as BIMElement).category !== 'Walls') {
            this.exitToSelect(i18n.t('tools.notify.editProfileWorksOnASingle'));
            return;
        }
        const wall = this.target as WallElement;
        const existing = wall.geometryData.profile;
        if (Array.isArray(existing) && existing.length >= 3) {
            this.points = existing.map(p => new THREE.Vector3(p[0], p[1], 0));
            this.updatePreview();
        } else {
            this.notify(i18n.t('tools.notify.clickPointsToDefineTheWall'), 'info');
        }
    }

    protected finish(): void {
        if (!this.context || !this.target) return;
        const wall = this.target as WallElement;
        const profile = this.points.map(p => [p.x, p.y] as [number, number]);

        const updates: Partial<WallElement> = {
            geometryData: {
                ...wall.geometryData,
                profile: profile as any,
                // Layer profiles would override the single profile in render;
                // clear them so the custom shape is visible.
                layerProfiles: undefined,
            },
        };
        this.context.bimStore.updateElement(wall.id, updates as any);
        this.notify(i18n.t('tools.notify.profileApplied', { wall: wall.name ?? 'wall', count: profile.length }), 'success');
        this.context.appStore.setToolPreview(null);
        this.exitToSelect();
    }
}

/* ==================== SURFACE: Edit Boundary ==================== */

/**
 * Edit Boundary / Sketch (Floors, Roofs, Ceilings, Toposolids): re-draws the
 * element's outer boundary polygon.
 */
export class EditBoundaryTool extends PolygonSketchTool {
    id = 'CTX_EDIT_BOUNDARY';
    name = 'Edit Boundary';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        this.onSketchPointerDown(event);
    }

    activate(context: any): void {
        super.activate(context);
        if (!this.beginSelection()) return;
        const el = this.target as BIMElement;
        const boundary = getSurfaceBoundary(el);
        if (!boundary) {
            this.exitToSelect(i18n.t('tools.notify.selectedElementHasNoBoundaryTo'));
            return;
        }
        this.points = boundary.map(p => new THREE.Vector3(p[0], p[1], 0));
        this.updatePreview();
        this.notify(i18n.t('tools.notify.editTheBoundaryClickPointsClose'), 'info');
    }

    protected finish(): void {
        if (!this.context || !this.target) return;
        const el = this.target as any;
        const key = boundaryKey(el);
        const nextBoundary = this.points.map(p => [p.x, p.y] as [number, number]);

        this.context.bimStore.updateElement(el.id, {
            geometryData: { ...el.geometryData, [key]: nextBoundary },
        } as any);
        this.notify(i18n.t('tools.notify.boundaryUpdated'), 'success');
        this.context.appStore.setToolPreview(null);
        this.exitToSelect();
    }
}

/* ==================== FLOOR: shape editing tools ==================== */

/**
 * Shared logic for the slab-shape (sub-element) editors. Only Floors carry a
 * SlabShapeEditorData in this kernel; other surface categories get a notice.
 */
abstract class ShapeEditToolBase extends BaseTool {
    protected floorId: string | null = null;

    /** Fresh element read by id — shape commits replace element identity. */
    protected floorElement(): BIMElement | null {
        if (!this.floorId || !this.context) return null;
        return this.context.bimStore.project.elements.get(this.floorId) ?? null;
    }

    protected ensureFloorSurface(): boolean {
        const els = getSelectedElements();
        const floor = els.find(e => e.category === 'Floors');
        if (!floor) {
            this.exitToSelect(i18n.t('tools.notify.shapeEditingPointsSplitLinesApplies'));
            return false;
        }
        const boundary = getSurfaceBoundary(floor);
        if (!boundary) {
            this.exitToSelect(i18n.t('tools.notify.selectedFloorHasNoBoundary'));
            return false;
        }
        this.floorId = floor.id;
        return true;
    }

    protected currentShape() {
        const el = this.floorElement();
        const g = el ? (el as any).geometryData : null;
        const editor = g?.shapeEditor ?? { isEnabled: true, points: [], splitLines: [] };
        return { ...editor, isEnabled: true, points: [...editor.points], splitLines: [...(editor.splitLines ?? [])] };
    }

    protected commitShape(editor: unknown) {
        const el = this.floorElement();
        if (!this.context || !el) return;
        const g = (el as any).geometryData;
        this.context.bimStore.updateElement(el.id, {
            geometryData: { ...g, shapeEditor: editor },
        } as any);
    }

    protected exitToSelect(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }
}

/**
 * Add Point: inserts a regional slope control point on the floor surface.
 */
export class AddShapePointTool extends ShapeEditToolBase {
    id = 'CTX_ADD_POINT';
    name = 'Add Point';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const floor = this.floorElement();
        if (!floor) { this.exitToSelect(i18n.t('tools.notify.floorNoLongerAvailable')); return; }
        const point = this.getIntersection(event);
        if (!point) return;
        const boundary = getSurfaceBoundary(floor);
        if (!boundary || !pointInPolygon2D(point.x, point.y, boundary)) {
            this.notify(i18n.t('tools.notify.pointMustLandInsideTheFloor'), 'warning');
            return;
        }
        const editor = this.currentShape();
        const newPoint: SlabShapePoint = {
            id: uuidv4(),
            position: [Number(point.x.toFixed(3)), Number(point.y.toFixed(3))],
            offset: 0,
            isBoundary: false,
        };
        editor.points.push(newPoint);
        this.commitShape(editor);
        this.notify(i18n.t('tools.notify.shapePointAdded', { x: point.x.toFixed(2), y: point.y.toFixed(2) }), 'success');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    activate(context: any): void {
        super.activate(context);
        if (!this.ensureFloorSurface()) return;
        this.notify(i18n.t('tools.notify.clickInsideTheFloorToAdd'), 'info');
    }
}

/**
 * Add Split Line (creline): two clicks create a water-flow / break line whose
 * endpoints become boundary shape points.
 */
export class AddSplitLineTool extends ShapeEditToolBase {
    id = 'CTX_ADD_SPLIT_LINE';
    name = 'Add Split Line';
    cursor = 'crosshair';

    private picks: THREE.Vector3[] = [];

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const floor = this.floorElement();
        if (!floor) { this.exitToSelect(i18n.t('tools.notify.floorNoLongerAvailable2')); return; }
        const point = this.getIntersection(event);
        if (!point) return;
        const boundary = getSurfaceBoundary(floor);
        if (!boundary || !pointInPolygon2D(point.x, point.y, boundary)) {
            this.notify(i18n.t('tools.notify.bothEndpointsMustLandInsideThe'), 'warning');
            return;
        }
        this.picks.push(point.clone());
        if (this.picks.length === 2) {
            this.commit();
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private commit() {
        if (!this.context || !this.floorElement()) return;
        const [a, b] = this.picks;
        const editor = this.currentShape();
        // Endpoints are internal control points (isBoundary false): the slab
        // solver only inherits offsets for points matched to boundary vertices,
        // so split-line picks must join the internal set to be triangulated.
        const pa: SlabShapePoint = { id: uuidv4(), position: [Number(a.x.toFixed(3)), Number(a.y.toFixed(3))], offset: 0, isBoundary: false };
        const pb: SlabShapePoint = { id: uuidv4(), position: [Number(b.x.toFixed(3)), Number(b.y.toFixed(3))], offset: 0, isBoundary: false };
        editor.points.push(pa, pb);
        editor.splitLines.push({ startPointId: pa.id, endPointId: pb.id, isCrease: true });
        this.commitShape(editor);
        this.notify(i18n.t('tools.notify.splitLineAdded'), 'success');
        this.context.appStore.setActiveTool('select');
    }

    activate(context: any): void {
        super.activate(context);
        if (!this.ensureFloorSurface()) return;
        this.notify(i18n.t('tools.notify.clickTwoPointsToPlaceA'), 'info');
    }
}

/**
 * Edit Sub-Elements: picks an existing shape point for the OptionsBar offset
 * editor; clicking near a point selects it, clicking empty space deselects.
 */
export class EditSubElementsTool extends ShapeEditToolBase {
    id = 'CTX_EDIT_SUB_ELEMENTS';
    name = 'Edit Sub-Elements';
    cursor = 'pointer';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const floor = this.floorElement();
        if (!floor) { this.exitToSelect(i18n.t('tools.notify.floorNoLongerAvailable3')); return; }
        const point = this.getIntersection(event);
        if (!point) return;
        const g = (floor as any).geometryData;
        const points: SlabShapePoint[] = g.shapeEditor?.points ?? [];
        let bestIndex = -1;
        let bestDist = 0.5; // 50 cm pick radius
        points.forEach((p, i) => {
            const d = Math.hypot(p.position[0] - point.x, p.position[1] - point.y);
            if (d < bestDist) { bestDist = d; bestIndex = i; }
        });
        const session = useSubElementStore.getState();
        if (bestIndex >= 0) {
            session.setSession({ elementId: floor.id, mode: 'select', selectedPointIndex: bestIndex });
            this.notify(i18n.t('tools.notify.shapePointSelected', { index: bestIndex + 1 }), 'info');
        } else {
            session.setSession({ selectedPointIndex: null });
        }
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    activate(context: any): void {
        super.activate(context);
        if (!this.ensureFloorSurface()) return;
        const floor = this.floorElement();
        const g = floor ? (floor as any).geometryData : null;
        if (!(g?.shapeEditor?.points?.length ?? 0)) {
            this.notify(i18n.t('tools.notify.thisFloorHasNoShapePoints'), 'warning');
        }
        useSubElementStore.getState().setSession({ elementId: this.floorId, mode: 'select', selectedPointIndex: null });
    }

    deactivate(): void {
        // Session clears on leaving the tool; the OptionsBar editor follows.
        useSubElementStore.getState().clear();
        super.deactivate();
    }
}

/* ==================== FAMILY: Pick New Host ==================== */

/**
 * Pick New Host (doors/windows & wall-hosted components): click another wall
 * to re-host the selected element; the distance-along-host parameter is
 * recomputed from the click projection.
 */
export class PickNewHostTool extends BaseTool {
    id = 'CTX_PICK_HOST';
    name = 'Pick New Host';
    cursor = 'crosshair';

    private hosted: BIMElement | null = null;

    activate(context: any): void {
        super.activate(context);
        const els = getSelectedElements();
        const target = els.find(e => (e as any).geometryData?.hostWallId);
        if (!target) {
            this.notify(i18n.t('tools.notify.selectAHostedElementDoorWindow'), 'warning');
            this.context?.appStore.setActiveTool('select');
            return;
        }
        this.hosted = target;
        this.notify(i18n.t('tools.notify.clickTheNewHostWall'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.hosted || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, e => e.category === 'Walls');
        if (!hit) {
            this.notify(i18n.t('tools.notify.noWallFoundUnderTheCursor'), 'warning');
            return;
        }
        const g = (this.hosted as any).geometryData;
        const start = new THREE.Vector3(...(hit.element as any).geometryData.start);
        const end = new THREE.Vector3(...(hit.element as any).geometryData.end);
        const seg = end.clone().sub(start);
        const len2 = seg.lengthSq();
        const t = len2 > 0 ? Math.min(1, Math.max(0, hit.point.clone().sub(start).dot(seg) / len2)) : 0.5;

        this.context.bimStore.updateElement(this.hosted.id, {
            geometryData: { ...g, hostWallId: hit.element.id, position: Number(t.toFixed(4)) },
        } as any);
        this.notify(i18n.t('tools.notify.rehosted', { host: hit.element.name ?? 'wall' }), 'success');
        this.context.appStore.setActiveTool('select');
    }

    onPointerMove(): void { }
    onPointerUp(): void { }
}

/* ==================== RAILING ==================== */

/** Edit Path (Railing): rebuilds the railing walk line from clicks. */
export class EditRailingPathTool extends BaseTool {
    id = 'CTX_EDIT_RAILING_PATH';
    name = 'Edit Path';
    cursor = 'crosshair';

    private railing: BIMElement | null = null;
    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    activate(context: any): void {
        super.activate(context);
        const els = getSelectedElements();
        const rail = els.find(e => e.category === 'Railings');
        if (!rail) {
            this.exit(i18n.t('tools.notify.selectARailingFirst'));
            return;
        }
        this.railing = rail;
        const path = (rail as any).geometryData.path;
        if (Array.isArray(path) && path.length > 1) {
            this.points = path.map((p: number[]) => new THREE.Vector3(...p));
            this.preview();
        }
        this.notify(i18n.t('tools.notify.clickPathPointsEnterToFinish'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point, 0.1);
        if (this.points.length > 1 && snapped.distanceTo(this.points[this.points.length - 2]) < SURFACE_CLOSE_TOLERANCE) {
            this.finish();
            return;
        }
        this.points.push(snapped);
        this.preview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point, 0.1);
            this.preview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 2) this.finish();
            return;
        }
        super.onKeyDown(event);
    }

    private preview() {
        if (!this.context) return;
        const pts = this.currentPoint ? [...this.points, this.currentPoint] : this.points;
        this.context.appStore.setToolPreview({
            type: 'polygon',
            points: pts,
            closed: false,
            color: '#00b4ff',
        });
    }

    private finish() {
        if (!this.context || !this.railing) return;
        const g = (this.railing as any).geometryData;
        const path = this.points.map(p => [Number(p.x.toFixed(3)), Number(p.y.toFixed(3)), Number(p.z.toFixed(3))] as [number, number, number]);
        this.context.bimStore.updateElement(this.railing.id, {
            geometryData: { ...g, path },
        } as any);
        this.context.appStore.setToolPreview(null);
        this.notify(i18n.t('tools.notify.railingPathUpdated'), 'success');
        this.exit();
    }

    private exit(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }
}

/** Pick New Host (Railing): re-attach a railing to another stair/floor. */
export class PickRailingHostTool extends BaseTool {
    id = 'CTX_PICK_RAILING_HOST';
    name = 'Pick Railing Host';
    cursor = 'crosshair';

    private railing: BIMElement | null = null;

    activate(context: any): void {
        super.activate(context);
        const els = getSelectedElements();
        const rail = els.find(e => e.category === 'Railings');
        if (!rail) {
            this.exit(i18n.t('tools.notify.selectARailingFirst2'));
            return;
        }
        this.railing = rail;
        this.notify(i18n.t('tools.notify.clickTheNewHostStairOr'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.railing || event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, e =>
            e.category === 'Stairs' || e.category === 'Floors' || e.category === 'Roofs' || e.category === 'Toposolid');
        if (!hit) {
            this.notify(i18n.t('tools.notify.noStairOrFloorUnderThe'), 'warning');
            return;
        }
        const g = (this.railing as any).geometryData;
        this.context.bimStore.updateElement(this.railing.id, {
            geometryData: { ...g, hostId: hit.element.id },
        } as any);
        this.notify(i18n.t('tools.notify.railingHosted', { type: hit.element.type }), 'success');
        this.exit();
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private exit(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }
}

/* ==================== STAIR ==================== */

/**
 * Edit Stairs: click a Level to set the stair's base/top constraint by
 * proximity, then re-solve rise/run counts (BIM's stair editor subset).
 */
export class EditStairTool extends BaseTool {
    id = 'CTX_EDIT_STAIR';
    name = 'Edit Stairs';
    cursor = 'crosshair';

    private stair: BIMElement | null = null;

    activate(context: any): void {
        super.activate(context);
        const els = getSelectedElements();
        const stair = els.find(e => e.category === 'Stairs');
        if (!stair) {
            this.exit(i18n.t('tools.notify.selectAStairFirst'));
            return;
        }
        this.stair = stair;
        this.notify(i18n.t('tools.notify.clickNearALevelLineTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.stair || event.button !== 0) return;
        // Levels draw their own invisible hit plane (visible=false), which the
        // generic element picker filters out — resolve the level datum directly
        // from the scene hit chain instead.
        const hits = this.raycastSceneAtEvent(event);
        let levelId: string | null = null;
        let hitZ = 0;
        for (const hit of hits) {
            let obj: THREE.Object3D | null = hit.object;
            while (obj) {
                const id = obj.userData?.levelId as string | undefined;
                if (id && this.context.bimStore.project.levels.has(id)) {
                    levelId = id;
                    hitZ = hit.point.z;
                    break;
                }
                obj = obj.parent;
            }
            if (levelId) break;
        }
        if (!levelId) {
            this.notify(i18n.t('tools.notify.noLevelFoundUnderTheCursor'), 'warning');
            return;
        }
        const g = (this.stair as any).geometryData;
        const bounds = elementWorldBounds(this.stair);
        const midZ = bounds ? (bounds.min.z + bounds.max.z) / 2 : 0;
        const isTop = hitZ > midZ;
        const level = this.context.bimStore.project.levels.get(levelId);

        const next: Record<string, unknown> = isTop ? { topLevelId: levelId } : { baseLevelId: levelId };
        this.context.bimStore.updateElement(this.stair.id, {
            geometryData: { ...g, ...next },
        } as any);
        this.notify(i18n.t('tools.notify.levelSet', { position: isTop ? i18n.t('tools.notify.posTopLower') : i18n.t('tools.notify.posBaseLower'), level: level?.name ?? 'level' }), 'success');
        this.exit();
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private exit(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }
}

/* ==================== IMAGE: Crop ==================== */

/**
 * Crop Image (raster): two clicks define a crop rectangle in world space; the
 * renderer shows only the image area inside the crop.
 */
export class CropImageTool extends BaseTool {
    id = 'CTX_CROP_IMAGE';
    name = 'Crop Image';
    cursor = 'crosshair';

    private image: BIMElement | null = null;
    private firstCorner: THREE.Vector3 | null = null;

    activate(context: any): void {
        super.activate(context);
        const els = getSelectedElements();
        const img = els.find(e => e.category === 'Raster Images');
        if (!img) {
            this.exit(i18n.t('tools.notify.selectARasterImageFirst'));
            return;
        }
        this.image = img;
        this.notify(i18n.t('tools.notify.clickOneCornerOfTheCrop'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.image || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        if (!this.firstCorner) {
            this.firstCorner = point.clone();
            this.notify(i18n.t('tools.notify.nowClickTheOppositeCorner'), 'info');
            return;
        }
        const g = (this.image as any).geometryData;
        const pos = new THREE.Vector3(...(g.position as number[]));
        // Store crop in image-local meters (origin at image center, +X/+Y)
        const x0 = this.firstCorner.x - pos.x;
        const y0 = this.firstCorner.y - pos.y;
        const x1 = point.x - pos.x;
        const y1 = point.y - pos.y;
        if (Math.abs(x1 - x0) < 0.05 || Math.abs(y1 - y0) < 0.05) {
            this.notify(i18n.t('tools.notify.cropRegionIsTooSmall'), 'warning');
            this.firstCorner = null;
            return;
        }
        this.context.bimStore.updateElement(this.image.id, {
            geometryData: {
                ...g,
                crop: {
                    minX: Math.min(x0, x1), maxX: Math.max(x0, x1),
                    minY: Math.min(y0, y1), maxY: Math.max(y0, y1),
                },
            },
        } as any);
        this.firstCorner = null;
        this.notify(i18n.t('tools.notify.imageCropped'), 'success');
        this.exit();
    }

    onPointerMove(): void { }
    onPointerUp(): void { }

    private exit(message?: string): void {
        if (message) this.notify(message, 'warning');
        this.context?.appStore.setActiveTool('select');
    }
}
